import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { tenantStore } from "@/lib/api";

/**
 * Water Compliance section navigation context.
 *
 * Most sub-screens (Locations, Assets, Work Orders, Schedules, Alerts, etc.)
 * only make sense in the context of a chosen Client → Site. Showing every
 * client's locations/assets in one flat list is overwhelming and lets users
 * make mistakes (logging readings against the wrong site). This context
 * stores the active scope and persists it across reloads.
 *
 * Selecting a new client clears any previously-picked site so we never end
 * up with a mismatched (clientA, siteOfClientB) combo.
 */

/**
 * Site scope state. Three modes:
 *   - `null`  — site not yet picked, screens render "choose a site".
 *   - `'all'` — All sites for the active client. Screens render
 *               cross-site data (with a per-screen guard for users
 *               who don't have client-wide access).
 *   - number  — a specific site id.
 */
export type SiteScope = number | 'all' | null;

interface Ctx {
  clientId: number | null;
  siteId: number | null;        // The specific site id, or null in 'all'/unpicked modes.
  siteScope: SiteScope;         // Authoritative tri-state.
  setClient: (id: number | null) => void;
  setSite: (scope: SiteScope) => void;
  /** True when client is picked AND (site is picked OR all-sites is set). */
  scopeReady: boolean;
  /** True iff the user has chosen "All sites for this client". */
  allSites: boolean;
}

// Keyed by tenant: client/site ids belong to one tenant, and an unkeyed
// scope survived a tenant switch and queried the old tenant's ids (LG-F-26).
const tenantSuffix = () => String(tenantStore.get() ?? "none");
const KEY_CLIENT = () => `legionella.clientId.${tenantSuffix()}`;
const KEY_SITE   = () => `legionella.siteId.${tenantSuffix()}`;

const WaterCtx = createContext<Ctx | null>(null);

function readNum(key: string): number | null {
  const v = typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
  return v ? Number(v) : null;
}
function readScope(key: string): SiteScope {
  const v = typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
  if (v === 'all') return 'all';
  if (v && /^\d+$/.test(v)) return Number(v);
  return null;
}
function writeNum(key: string, v: number | null): void {
  if (typeof localStorage === "undefined") return;
  if (v == null) localStorage.removeItem(key); else localStorage.setItem(key, String(v));
}
function writeScope(key: string, v: SiteScope): void {
  if (typeof localStorage === "undefined") return;
  if (v === null)       localStorage.removeItem(key);
  else if (v === 'all') localStorage.setItem(key, 'all');
  else                  localStorage.setItem(key, String(v));
}

export function WaterContextProvider({ children }: { children: ReactNode }) {
  const [clientId, _setClient] = useState<number | null>(() => readNum(KEY_CLIENT()));
  const [siteScope, _setScope] = useState<SiteScope>(() => readScope(KEY_SITE()));

  const setClient = useCallback((id: number | null) => {
    _setClient(id);
    writeNum(KEY_CLIENT(), id);
    // Site belongs to a client — picking a different client invalidates it.
    _setScope(null);
    writeScope(KEY_SITE(), null);
  }, []);

  const setSite = useCallback((scope: SiteScope) => {
    _setScope(scope);
    writeScope(KEY_SITE(), scope);
  }, []);

  // Keep storage in sync if the state ever drifts (defensive — React's
  // strict-mode double-mount can rerun the initializer).
  useEffect(() => { writeNum(KEY_CLIENT(), clientId); }, [clientId]);
  useEffect(() => { writeScope(KEY_SITE(), siteScope); }, [siteScope]);

  const siteId = typeof siteScope === 'number' ? siteScope : null;
  const allSites = siteScope === 'all';

  const value: Ctx = {
    clientId, siteId, siteScope, setClient, setSite,
    scopeReady: clientId != null && siteScope != null,
    allSites,
  };

  return <WaterCtx.Provider value={value}>{children}</WaterCtx.Provider>;
}

export function useWaterContext(): Ctx {
  const ctx = useContext(WaterCtx);
  if (!ctx) throw new Error("useWaterContext must be used inside WaterContextProvider");
  return ctx;
}
